import { StrictMode, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@repo/ui/lib/theme-provider';
import { Toaster } from '@repo/ui/components/molecules/toaster';
import i18 from '@/lib/i18n';
import { queryClient } from '@/api/common/client';
import { TenantProvider } from '@/lib/auth/tenant-context';
import { MasterDataProvider } from '@/api/master-data/provider';
import { WebSocketProvider } from '@/providers/websocket-provider';
import { AppVersionWatcher } from '@/lib/version/app-version-watcher';
import { WebTableConfigProvider } from '@/providers/table-config-provider';
import { WebFormConfigProvider } from '@/providers/form-config-provider';

interface Props {
  /**
   * Pagina pubblica col token nel path (link del rapportino, firma del
   * contratto): chi la apre non ha un account.
   */
  publicTokenPage: boolean;
  children: ReactNode;
}

/**
 * App autenticata: sessione e tenant (`/auth/me`), websocket, dati di
 * riferimento del tenant e avviso di nuova versione.
 */
function AuthenticatedAppProviders({ children }: { children: ReactNode }) {
  return (
    <TenantProvider>
      <WebSocketProvider>
        <MasterDataProvider>
          <StrictMode>
            <AppVersionWatcher />
            <WebTableConfigProvider>
              <WebFormConfigProvider>{children}</WebFormConfigProvider>
            </WebTableConfigProvider>
            <Toaster />
          </StrictMode>
        </MasterDataProvider>
      </WebSocketProvider>
    </TenantProvider>
  );
}

/**
 * Pagina pubblica col token: nessun provider dell'app autenticata. Niente
 * `/auth/me` né websocket per chi non ha un account, niente dati del tenant,
 * e niente «Aggiorna» della nuova versione davanti a un cliente (la pagina si
 * ricarica da sé alla prossima apertura del link). I form usano il default
 * del package (si può scrivere).
 */
function PublicPageProviders({ children }: { children: ReactNode }) {
  return (
    <StrictMode>
      <WebTableConfigProvider>{children}</WebTableConfigProvider>
      <Toaster />
    </StrictMode>
  );
}

export function AppProviders({ publicTokenPage, children }: Props) {
  const Providers = publicTokenPage
    ? PublicPageProviders
    : AuthenticatedAppProviders;
  return (
    <ThemeProvider>
      <I18nextProvider i18n={i18}>
        <QueryClientProvider client={queryClient}>
          <Providers>{children}</Providers>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}
